import { defineStore } from 'pinia'
import AuthService from '@/services/AuthService'
import { isAdminRole } from '@/lib/auth'
import { parsePermissionTree } from '@/lib/parsePermissionTree'
import { getSecureItem, setSecureItem } from '@/lib/secureStorage'
import type {
  Organization,
  PermissionTree,
  RegisterRequest,
  RegistrationResult,
  User,
} from '@/types/auth'

const authService = new AuthService()

export interface AuthStoreState {
  refresh: string | null
  access: string | null
  user: User | null
  organization: Organization | null
  permissions: PermissionTree | null
  orgPermissions: PermissionTree | null
  roles: string[]
  sessionHydrated: boolean
}

/**
 * All auth data (tokens, user, org, permissions) lives in the encrypted secure
 * storage layer (see src/lib/secureStorage.ts), not plain localStorage — so this
 * store intentionally has no `persist` option. `hydrateSecureStorage()` must have
 * already run (main.ts does this before mounting) for these reads to see data.
 */
function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = getSecureItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

export const useAuthStore = defineStore('authStore', {
  state: (): AuthStoreState => ({
    refresh: getSecureItem('refreshToken'),
    access: getSecureItem('accessToken'),
    user: readJson<User | null>('user', null),
    organization: readJson<Organization | null>('organization', null),
    permissions: readJson<PermissionTree | null>('permissions', null),
    orgPermissions: readJson<PermissionTree | null>('orgPermissions', null),
    roles: readJson<string[]>('roles', []),
    sessionHydrated: false,
  }),
  actions: {
    async login(email: string, password: string) {
      const response = await authService.login(email, password)

      this.access = response.accessToken
      this.refresh = response.refreshToken

      setSecureItem('accessToken', response.accessToken)
      setSecureItem('refreshToken', response.refreshToken)

      const context = await this.hydrateAuthContext(parsePermissionTree(response.permissions))
      return {
        user: context.user,
        organization: context.organization,
        permissions: context.permissions,
        orgPermissions: context.orgPermissions,
      }
    },

    async hydrateAuthContext(fallbackPermissions: PermissionTree | null = null) {
      const user = await authService.getCurrentUser()
      this.user = user
      this.roles = user.roles

      // The API is the source of truth for the org name/slug; stored values only cover
      // older backends that don't return them yet.
      const storedOrg = readJson<Organization | null>('organization', null)
      const orgSlug = user.orgSlug || getSecureItem('client') || storedOrg?.orgSlug || user.orgId
      this.organization = {
        orgId: user.orgId,
        orgSlug,
        name: user.orgName || storedOrg?.name,
      }

      setSecureItem('user', JSON.stringify(user))
      setSecureItem('roles', JSON.stringify(user.roles))
      setSecureItem('organization', JSON.stringify(this.organization))
      setSecureItem('client', orgSlug)

      const [permissions, orgPermissions] = await Promise.all([
        this.loadPermissions(user.userId, fallbackPermissions),
        this.loadOrgPermissions(),
      ])

      this.sessionHydrated = true

      return {
        user,
        organization: this.organization,
        permissions,
        orgPermissions,
      }
    },

    async loadPermissions(userId?: string, fallback: PermissionTree | null = null) {
      const targetUserId = userId ?? this.user?.userId
      if (!targetUserId) {
        return this.permissions
      }

      try {
        const response = await authService.getUserPermissions(targetUserId)
        this.permissions = response.permissions
      } catch (error) {
        console.error('Failed to load permissions:', error)
        this.permissions = fallback ?? this.permissions ?? {}
      }

      setSecureItem('permissions', JSON.stringify(this.permissions ?? {}))
      return this.permissions
    },

    async loadOrgPermissions() {
      try {
        this.orgPermissions = await authService.getOrgPermissions()
      } catch (error) {
        console.error('Failed to load org permissions:', error)
        this.orgPermissions = this.orgPermissions ?? {}
      }

      setSecureItem('orgPermissions', JSON.stringify(this.orgPermissions ?? {}))
      return this.orgPermissions
    },

    async register(data: RegisterRequest): Promise<RegistrationResult> {
      const result = await authService.register(data)
      setSecureItem('client', result.orgSlug)
      setSecureItem(
        'organization',
        JSON.stringify({
          orgId: result.organizationId,
          orgSlug: result.orgSlug,
          name: data.orgName,
        }),
      )
      return result
    },

    async verifyEmail(token: string): Promise<void> {
      await authService.verifyEmail(token)
    },

    async forgotPassword(email: string): Promise<void> {
      await authService.forgotPassword(email)
    },

    async resetPassword(token: string, newPassword: string): Promise<void> {
      await authService.resetPassword(token, newPassword)
    },

    async acceptInvite(token: string, password: string): Promise<void> {
      await authService.acceptInvite(token, password)
    },

    async changePassword(currentPassword: string, newPassword: string): Promise<void> {
      await authService.changePassword(currentPassword, newPassword)
    },

    getViewPermissions(view: string) {
      const permissions = readJson<PermissionTree>('permissions', {})
      return permissions[view] ?? null
    },

    getOrgViewPermissions(view: string) {
      const orgPermissions = readJson<PermissionTree>('orgPermissions', {})
      return orgPermissions[view] ?? null
    },

    getObjectPermissions(view: string, obj: string) {
      const viewPermissions = this.getViewPermissions(view)
      if (!viewPermissions) {
        return null
      }
      return viewPermissions[obj] ?? null
    },

    async logout() {
      try {
        // Lazy so the Firebase SDK never lands in the main bundle.
        const { usePushNotifications } = await import('@/composables/usePushNotifications')
        await usePushNotifications().disable()
      } catch {
        /* non-fatal — the token expires on its own */
      }
      try {
        await authService.logout()
      } finally {
        this.refresh = null
        this.access = null
        this.user = null
        this.organization = null
        this.permissions = null
        this.orgPermissions = null
        this.roles = []
        this.sessionHydrated = false
      }
    },
  },
  getters: {
    getUser: (state) => state.user,
    getPermissions: (state) => state.permissions,
    getOrgPermissions: (state) => state.orgPermissions,
    getOrganization: (state) => state.organization,
    getRoles: (state) => state.roles,
    isAdmin: (state) => isAdminRole(state.roles),
    orgRouteSlug: (state) => state.organization?.orgSlug || getSecureItem('client'),
    /**
     * Must read `state.access`/`state.refresh` so this getter re-evaluates when tokens
     * change (Pinia getters are Vue `computed()`s under the hood — a getter with no
     * reactive dependency evaluates once and stays cached, which previously caused this
     * to stay stuck at `false` after a fresh login and bounce the user back to /auth/).
     */
    isAuthenticated: (state) => {
      void state.access
      void state.refresh
      return authService.isAuthenticated()
    },
  },
})
